import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { debounce } from "lodash";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { HeuristicEvaluationResponse } from "@/generated";
import type { AxiosError } from "axios";
import { SpotToast } from "@/components/SpotToast";
import {
  isAllModelsDisabledError,
  PromptJobTimeoutError,
} from "@/services/promptHeuristicJobs";

interface BackendError {
  identifier?: string;
  message?: string;
  status_code?: number;
}

function parseHeuristicError(error: unknown): string | null {
  if (!error) return null;
  const status = (error as AxiosError<BackendError>).response?.status;

  if (isAllModelsDisabledError(error)) {
    return "No AI models are available for this workspace. Contact your administrator.";
  }
  if (error instanceof PromptJobTimeoutError || status === 504) {
    return "Analysis timed out. Please try again.";
  }
  return "Analysis failed. Please try again.";
}

// Module-level cache: persists results across unmounts (tab switches)
const MAX_CACHE_ENTRIES = 50;
const resultCache = new Map<string, HeuristicEvaluationResponse>();

function evictIfNeeded() {
  while (resultCache.size > MAX_CACHE_ENTRIES) {
    const oldest = resultCache.keys().next().value;
    if (oldest !== undefined) resultCache.delete(oldest);
    else break;
  }
}

// Persist cache to sessionStorage so results survive page refresh
const CACHE_STORAGE_KEY = "heuristic-result-cache";

if (typeof window !== "undefined") {
  try {
    const stored = sessionStorage.getItem(CACHE_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Record<
        string,
        HeuristicEvaluationResponse
      >;
      for (const [key, value] of Object.entries(parsed)) {
        resultCache.set(key, value);
      }
      evictIfNeeded();
    }
  } catch {
    // Ignore parse errors
  }
}

function persistCache() {
  try {
    const obj: Record<string, HeuristicEvaluationResponse> = {};
    for (const [key, value] of resultCache.entries()) {
      obj[key] = value;
    }
    sessionStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(obj));
  } catch {
    // Ignore storage errors
  }
}

// Simple string hash for cache key generation
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0; // Convert to 32bit integer
  }
  return hash.toString(36);
}

interface UseHeuristicEvaluationProps {
  text: string;
  enabled?: boolean;
  context?: string;
  heuristicTypes?: string[];
  minLength?: number;
  debounceMs?: number;
}

interface EvaluationEntry {
  hash: string;
  results: HeuristicEvaluationResponse;
}

interface UseHeuristicEvaluationResult {
  results: HeuristicEvaluationResponse | null;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  isOutdated: boolean;
  canAnalyse: boolean;
  triggerEvaluation: (silent?: boolean) => void;
  scheduleReanalysis: () => void;
  revertAnalysis: () => void;
  showPopover: boolean;
  setShowPopover: (show: boolean) => void;
}

export function useHeuristicEvaluation({
  text,
  enabled = false,
  context,
  heuristicTypes,
  minLength = 200,
  debounceMs = 2500,
}: UseHeuristicEvaluationProps): UseHeuristicEvaluationResult {
  const { spotGroup } = useSpotUser();

  // Compute hash for a given text (used for both init and debounced updates).
  // Hash the trimmed text so pure-whitespace serialization noise — e.g. the
  // trailing newline a rich-text editor appends when it re-serializes on blur /
  // modal close — does NOT make a just-computed score look outdated. The score
  // is only stale when the meaningful content changes.
  const computeHash = useCallback(
    (promptText: string): string | null => {
      const normalized = promptText.trim();
      if (!normalized || normalized.length < minLength) return null;
      return simpleHash(
        normalized + (context || "") + (heuristicTypes?.join(",") || "")
      );
    },
    [context, heuristicTypes, minLength]
  );

  // currentTextHash — always reflects the latest debounced text
  const [currentTextHash, setCurrentTextHash] = useState<string | null>(() =>
    enabled ? computeHash(text) : null
  );
  // activeHash — the hash that React Query actually uses
  // Warm-start from module cache on mount (e.g., after tab switch)
  const [activeHash, setActiveHash] = useState<string | null>(() => {
    if (!enabled) return null;
    const hash = computeHash(text);
    return hash !== null && resultCache.has(hash) ? hash : null;
  });
  // Whether we've completed at least one evaluation
  const [hasEvaluated, setHasEvaluated] = useState(() => {
    if (!enabled) return false;
    const hash = computeHash(text);
    return hash !== null && resultCache.has(hash);
  });
  // Hash of the last completed evaluation — survives scheduleReanalysis nulling activeHash
  const [lastEvaluatedHash, setLastEvaluatedHash] = useState<string | null>(
    () => {
      if (!enabled) return null;
      const hash = computeHash(text);
      return hash !== null && resultCache.has(hash) ? hash : null;
    }
  );
  // Flag for auto-trigger after refinement (scheduleReanalysis)
  const [pendingReanalysis, setPendingReanalysis] = useState(false);
  const [showPopover, setShowPopover] = useState(false);

  // Keep a ref to the latest currentTextHash for the manual trigger callback
  const currentTextHashRef = useRef(currentTextHash);
  currentTextHashRef.current = currentTextHash;

  // Keep a ref to the latest error state so triggerEvaluation can read it without stale closure
  const isQueryErrorRef = useRef(false);
  // Keep a ref to refetch so triggerEvaluation can call it without a forward reference
  const refetchRef = useRef<() => void>(() => undefined);
  // Track whether the current evaluation was manually triggered (vs auto on load)
  const isManualTriggerRef = useRef(false);

  // Keep a ref to the latest text for immediate hash computation in triggerEvaluation
  const textRef = useRef(text);
  textRef.current = text;

  // Persist last results across activeHash changes (fallback during reanalysis)
  // Warm-start from module cache for immediate display on mount (e.g., after page refresh)
  const initHash = enabled ? computeHash(text) : null;
  const lastResultsRef = useRef<HeuristicEvaluationResponse | null>(
    initHash !== null ? (resultCache.get(initHash) ?? null) : null
  );

  // Stack of evaluation results for revert support
  const resultsStackRef = useRef<EvaluationEntry[]>(
    initHash !== null && resultCache.has(initHash)
      ? [{ hash: initHash, results: resultCache.get(initHash)! }]
      : []
  );

  // Immediate (non-debounced) hash for instant staleness detection
  const immediateHash = useMemo(() => computeHash(text), [computeHash, text]);

  // Debounce text hash update
  const debouncedUpdateHash = useMemo(
    () =>
      debounce((promptText: string) => {
        setCurrentTextHash(computeHash(promptText));
      }, debounceMs),
    [computeHash, debounceMs]
  );

  // Update currentTextHash on every text change
  useEffect(() => {
    if (enabled) {
      debouncedUpdateHash(text);
    }
    return () => {
      debouncedUpdateHash.cancel();
    };
  }, [text, enabled, debouncedUpdateHash]);

  // Reset active query when text drops below threshold.
  // Keep lastEvaluatedHash and lastResultsRef so the chart stays greyed out (isOutdated).
  useEffect(() => {
    if (!text.trim() || text.length < minLength) {
      setActiveHash(null);
      setHasEvaluated(false);
      setPendingReanalysis(false);
    }
  }, [text, minLength]);

  // Auto-trigger ONLY after scheduleReanalysis (post-refinement)
  useEffect(() => {
    if (pendingReanalysis && currentTextHash !== null && enabled) {
      setActiveHash(currentTextHash);
      setPendingReanalysis(false);
    }
  }, [currentTextHash, pendingReanalysis, enabled]);

  // Auto-trigger first analysis when debounced hash settles and no cached results.
  // hasAutoTriggered ref ensures it only fires once per mount.
  const hasAutoTriggered = useRef(false);
  useEffect(() => {
    if (
      !hasAutoTriggered.current &&
      enabled &&
      !hasEvaluated &&
      activeHash === null &&
      !pendingReanalysis &&
      currentTextHash !== null
    ) {
      hasAutoTriggered.current = true;
      setActiveHash(currentTextHash);
    }
  }, [enabled, currentTextHash, hasEvaluated, activeHash, pendingReanalysis]);

  // Manual trigger — compute hash immediately (bypass debounce) and set active.
  // If in error state, the query key hasn't changed so we must call refetch() directly.
  const triggerEvaluation = useCallback(
    (silent = false) => {
      debouncedUpdateHash.cancel();
      const hash = computeHash(textRef.current);
      if (hash !== null) {
        if (!silent) isManualTriggerRef.current = true;
        setCurrentTextHash(hash);
        if (isQueryErrorRef.current) {
          refetchRef.current();
        } else {
          setActiveHash(hash);
        }
      }
    },
    [computeHash, debouncedUpdateHash]
  );

  // Schedule auto-reanalysis after refinement
  // Nulls currentTextHash to prevent premature re-trigger with stale hash
  const scheduleReanalysis = useCallback(() => {
    setHasEvaluated(false);
    setActiveHash(null);
    setCurrentTextHash(null);
    setPendingReanalysis(true);
  }, []);

  // React Query for heuristic evaluation with activeHash as cache key
  const {
    data: heuristicResults,
    isLoading: isLoadingHeuristics,
    isError: isQueryError,
    error: queryError,
    refetch: queryRefetch,
  } = useRemoteData(
    DATA_KEYS.HEURISTICS_EVALUATION,
    {
      path: { group_id: spotGroup.id },
      body: {
        prompt: text,
        context: context || undefined,
        heuristic_types: heuristicTypes || undefined,
      },
    },
    {
      enabled: enabled && activeHash !== null && text.length >= minLength,
      queryKey: [DATA_KEYS.HEURISTICS_EVALUATION, spotGroup.id, activeHash],
      retry: (failureCount, error) => {
        // Don't retry permanent errors like all_models_disabled
        if (isAllModelsDisabledError(error)) {
          return false;
        }
        return failureCount < 1;
      },
    }
  );

  isQueryErrorRef.current = isQueryError;
  refetchRef.current = queryRefetch;

  // Toast on evaluation error — only for manually triggered requests
  useEffect(() => {
    if (isQueryError && queryError && isManualTriggerRef.current) {
      isManualTriggerRef.current = false;
      SpotToast.error(
        parseHeuristicError(queryError) ?? "Analysis failed. Please try again."
      );
    }
  }, [isQueryError, queryError]);

  // Update caches when results arrive
  useEffect(() => {
    if (heuristicResults) {
      lastResultsRef.current = heuristicResults;
      if (activeHash) {
        resultCache.set(activeHash, heuristicResults);
        evictIfNeeded();
        persistCache();
        setLastEvaluatedHash(activeHash);
        // Push to results stack (avoid duplicate if same hash is already on top)
        const stack = resultsStackRef.current;
        if (stack.length === 0 || stack[stack.length - 1].hash !== activeHash) {
          stack.push({ hash: activeHash, results: heuristicResults });
        }
      }
      if (!hasEvaluated) {
        setHasEvaluated(true);
      }
    }
  }, [heuristicResults, activeHash, hasEvaluated]);

  // Revert to previous analysis results (pop the stack)
  const revertAnalysis = useCallback(() => {
    debouncedUpdateHash.cancel();
    setPendingReanalysis(false);
    const stack = resultsStackRef.current;
    if (stack.length > 1) {
      stack.pop();
      const prev = stack[stack.length - 1];
      lastResultsRef.current = prev.results;
      setLastEvaluatedHash(prev.hash);
      setActiveHash(prev.hash);
      setCurrentTextHash(prev.hash);
      setHasEvaluated(true);
    }
  }, [debouncedUpdateHash]);

  // Use fallback results when current query has no data (e.g., during reanalysis)
  const effectiveResults = heuristicResults ?? lastResultsRef.current;

  const canAnalyse = Boolean(text.trim()) && text.trim().length >= minLength;

  const isOutdated =
    lastEvaluatedHash !== null && immediateHash !== lastEvaluatedHash;

  return {
    results: effectiveResults,
    isLoading: isLoadingHeuristics || pendingReanalysis,
    isError: isQueryError,
    errorMessage: isQueryError ? parseHeuristicError(queryError) : null,
    isOutdated,
    canAnalyse,
    triggerEvaluation,
    scheduleReanalysis,
    revertAnalysis,
    showPopover,
    setShowPopover,
  };
}
